import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Wrench, TrendingUp, AlertTriangle, ArrowRight } from "lucide-react";
import { BarChart, Bar, LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { VendorJumpTicketsDialog } from "@/components/VendorJumpTicketsDialog";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

export function TicketSpendCard({ compact = false, defaultPropertyId = "all" }) {
  const [scope, setScope] = useState(defaultPropertyId);
  const { properties } = useProperty();
  const [data, setData] = useState(null);
  const [jump, setJump] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [drillOpen, setDrillOpen] = useState(false);

  useEffect(() => { setScope(defaultPropertyId || "all"); }, [defaultPropertyId]);

  const load = useCallback(async () => {
    try {
      const params = { year };
      if (scope && scope !== "all") params.property_id = scope;
      const jumpParams = {};
      if (scope && scope !== "all") jumpParams.property_id = scope;
      const [s, j] = await Promise.all([
        api.get("/admin/tickets/spend/summary", { params }),
        api.get("/admin/tickets/spend/vendor-jump", { params: jumpParams }),
      ]);
      setData(s.data);
      setJump(j.data);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load ticket spend"); }
  }, [year, scope]);

  useEffect(() => { load(); }, [load]);

  return (
    <Card className="p-5 border-white/10 bg-card" data-testid="ticket-spend-card">
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Wrench className="h-4 w-4 text-orange-400" />
          <div>
            <div className="text-xs uppercase tracking-widest text-zinc-500">Maintenance</div>
            <div className="text-lg font-semibold font-display">Ticket Spend</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-24 bg-white/[0.04] border-white/10 h-8" data-testid="ticket-spend-year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
              {[0, 1, 2].map((i) => {
                const y = new Date().getFullYear() - i;
                return <SelectItem key={y} value={String(y)}>{y}</SelectItem>;
              })}
            </SelectContent>
          </Select>
          {!compact && (
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="w-44 bg-white/[0.04] border-white/10 h-8" data-testid="ticket-spend-scope">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                <SelectItem value="all">All communities</SelectItem>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {jump?.flagged && (
        <button
          type="button"
          onClick={() => setDrillOpen(true)}
          className="w-full text-left rounded-md border border-amber-500/30 bg-amber-500/[0.05] hover:bg-amber-500/[0.09] transition px-3 py-2 mb-3 flex items-start gap-2 group"
          data-testid="ticket-spend-jump"
        >
          <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-xs text-zinc-200 leading-relaxed flex-1 min-w-0">
            <div className="font-medium text-amber-200">
              <span data-testid="ticket-spend-jump-vendor">{jump.flagged.vendor}</span> spend jumped
              {" "}
              <span className="tabular-nums" data-testid="ticket-spend-jump-pct">
                +{jump.flagged.pct_change}%
              </span>{" "}
              ({money(jump.flagged.prior_amount)} → {money(jump.flagged.current_amount)}) from {jump.prior_quarter} to {jump.current_quarter}
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              {jump.flagged.prior_jobs} → {jump.flagged.current_jobs} jobs · +{money(jump.flagged.delta)} vs prior quarter
            </div>
          </div>
          <ArrowRight className="h-3 w-3 text-amber-400 shrink-0 mt-1 opacity-0 group-hover:opacity-100 transition" />
        </button>
      )}

      <VendorJumpTicketsDialog
        open={drillOpen && !!jump?.flagged}
        onOpenChange={setDrillOpen}
        vendor={jump?.flagged?.vendor}
        quarter="current"
        propertyId={scope}
      />

      {!data ? (
        <div className="text-xs text-zinc-500 italic py-6 text-center">Loading spend…</div>
      ) : data.total === 0 ? (
        <div className="text-xs text-zinc-500 italic py-8 text-center" data-testid="ticket-spend-empty">
          No resolved tickets with a captured cost for {data.year}
          {scope !== "all" ? " on this community" : ""}. Switch the year or community above,
          or add a final cost via the vendor scorecard feedback to see spend flow here.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            <StatCard label="Total spend" value={money(data.total)} highlight />
            <StatCard label="Tickets closed" value={data.tickets_counted} />
            <StatCard label="Top category" value={data.by_category[0]?.category || "—"} />
            <StatCard label="Top vendor" value={data.by_vendor[0]?.vendor || "—"} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="rounded-md border border-white/5 p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-1">
                <TrendingUp className="h-3 w-3" /> Monthly spend · {data.year}
              </div>
              <div style={{ width: "100%", height: 180 }} data-testid="ticket-spend-monthly">
                <ResponsiveContainer minHeight={180}>
                  <LineChart data={data.by_month} margin={{ top: 6, right: 6, bottom: 4, left: -14 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="month" tick={{ fill: "#a1a1aa", fontSize: 10 }} />
                    <YAxis tick={{ fill: "#a1a1aa", fontSize: 10 }}
                           tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                    <Tooltip contentStyle={{ background: "#0a0a0b", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }}
                             formatter={(v) => money(v)} />
                    <Line type="monotone" dataKey="amount" stroke="#f97316" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-md border border-white/5 p-3">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-1">
                <Wrench className="h-3 w-3" /> Spend by category
              </div>
              <div style={{ width: "100%", height: 180 }} data-testid="ticket-spend-category">
                <ResponsiveContainer minHeight={180}>
                  <BarChart data={data.by_category.slice(0, 6)} margin={{ top: 6, right: 6, bottom: 4, left: -14 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="category" tick={{ fill: "#a1a1aa", fontSize: 10 }} interval={0} />
                    <YAxis tick={{ fill: "#a1a1aa", fontSize: 10 }}
                           tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                    <Tooltip contentStyle={{ background: "#0a0a0b", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }}
                             formatter={(v) => money(v)} />
                    <Bar dataKey="amount" fill="#818cf8" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {data.by_vendor?.length > 0 && (
            <div className="rounded-md border border-white/5 p-3 mt-3" data-testid="ticket-spend-vendors">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Top vendors</div>
              <div className="space-y-1">
                {data.by_vendor.slice(0, 5).map((v) => (
                  <div key={v.vendor_id || v.vendor} className="flex items-center justify-between text-xs">
                    <span className="text-zinc-100 truncate">{v.vendor || "Unassigned"}</span>
                    <div className="flex items-center gap-3 shrink-0">
                      <Badge variant="outline" className="border-white/10 text-zinc-400">
                        {v.jobs} job{v.jobs === 1 ? "" : "s"}
                      </Badge>
                      <span className="tabular-nums text-white w-20 text-right">{money(v.amount)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

function StatCard({ label, value, highlight }) {
  return (
    <div className={"rounded-md border px-3 py-2 " +
      (highlight ? "border-orange-500/40 bg-orange-500/[0.05]" : "border-white/5 bg-white/[0.01]")}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={"text-sm tabular-nums " + (highlight ? "text-orange-300 font-semibold" : "text-zinc-100")}>{value}</div>
    </div>
  );
}

export default TicketSpendCard;
